/* The app's query cache: its defaults, and the one place a failed query or mutation reaches
   the user. Here, not in app/providers.tsx, so a feature's test can drive the same caches the
   app runs with. */
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { OUT_OF_CREDITS } from "@/shared/api/billing";
import { ApiError } from "@/shared/api/errors";
import { openBilling } from "@/shared/components/UpgradeButton";

/* The one place a failed query or mutation becomes something the user can see.
   Before this existed, a query with no explicit `error` branch failed in total
   silence — the screen simply never changed (00-PLAN.md, the no-silent rule).

   `id` dedupes: a retrying query or a refetch loop fires onError once per
   attempt, and without a stable id that is one toast per attempt.

   A caller that renders the failure itself sets `meta: {silent: true}` and owns
   it from there — `shared/api/settings.ts` is the example.

   Out of credits is the one refusal with a way out, so its toast carries it:
   every way a run starts (chat, evals, a task run now) is refused with it. */
function report(err: unknown, id: string, meta?: Record<string, unknown>): void {
  console.error(err);
  const api = err instanceof ApiError ? err : null;
  if (api?.debug) console.error(api.debug);
  if (meta?.silent) return;
  toast.error(err instanceof Error ? err.message : "Something went wrong", {
    id,
    description: api?.request_id ? `ref ${api.request_id}` : undefined,
    action: api?.code === OUT_OF_CREDITS ? { label: "Upgrade", onClick: openBilling } : undefined,
  });
}

export const createQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        refetchOnWindowFocus: false,
      },
    },
    queryCache: new QueryCache({
      onError: (err, query) => report(err, query.queryHash, query.meta),
    }),
    mutationCache: new MutationCache({
      onError: (err, _vars, _ctx, mutation) =>
        report(err, `mutation:${mutation.mutationId}`, mutation.meta),
    }),
  });
